import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface BalanceData {
  currentBalance: number;
  totalDeposited: number;
  totalConsumed: number;
  autoRenewal: {
    enabled: boolean;
    amount: number | null;
    threshold: number | null;
  };
  hasPaymentMethod: boolean;
}

export interface Transaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  paymentMethod: string | null;
  status: string;
  description: string | null;
  createdAt: string;
}

export interface TopUpResponse {
  transactionId: string;
  stripeClientSecret: string | null;
  pixQrCode: string | null;
  pixExpiresAt: string | null;
  boletoUrl: string | null;
  boletoBarcode: string | null;
}

export interface ProjectionTier {
  amount: number;
  estimatedImpressions: number;
  estimatedClicks: number;
  estimatedCtr: number;
  estimatedQuotes: number;
  estimatedRoi: number | null;
  daysOfCoverage: number;
}

export interface ProjectionData {
  basedOnHistoricalData: boolean;
  avgCpm: number;
  avgCpc: number;
  quoteConversionRate: number;
  tiers: ProjectionTier[];
}

export function useBalance() {
  return useQuery<BalanceData>({
    queryKey: ['partner-balance'],
    queryFn: async () => {
      const res = await api.get('/partners/my/balance');
      return res.data;
    },
    refetchInterval: 60_000,
  });
}

export function useTransactions(page: number = 1, type?: string) {
  return useQuery<{ items: Transaction[]; total: number }>({
    queryKey: ['partner-transactions', page, type],
    queryFn: async () => {
      const res = await api.get('/partners/my/transactions', {
        params: { page, limit: 20, ...(type ? { type } : {}) },
      });
      return res.data;
    },
  });
}

export function useTopUp() {
  const queryClient = useQueryClient();
  return useMutation<TopUpResponse, Error, { amount: number; paymentMethod: 'pix' | 'boleto' | 'credit_card' }>({
    mutationFn: async (payload) => {
      const res = await api.post('/partners/my/top-up', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-balance'] });
      queryClient.invalidateQueries({ queryKey: ['partner-transactions'] });
    },
  });
}

export function useProjection(amount: number) {
  return useQuery<ProjectionData>({
    queryKey: ['investment-projection', amount],
    queryFn: async () => {
      const res = await api.get('/partners/my/investment-projection', {
        params: { amount },
      });
      return res.data;
    },
    enabled: amount >= 100,
    staleTime: 30 * 60 * 1000,
  });
}

export function useUpdateAutoRenewal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { enabled: boolean; amount?: number; threshold?: number; paymentMethodId?: string }) => {
      const res = await api.put('/partners/my/auto-renewal', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-balance'] });
    },
  });
}

export function useSetupPaymentMethod() {
  return useMutation<{ clientSecret: string }>({
    mutationFn: async () => {
      const res = await api.post('/partners/my/setup-payment-method');
      return res.data;
    },
  });
}
